import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../types';
import { Rabbit, LogOut, User as UserIcon } from 'lucide-react';

interface HeaderProps {
  user: User;
  onSignOut: () => void;
}

export const Header: React.FC<HeaderProps> = ({ user, onSignOut }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 w-full bg-rutabaga-bg/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center cursor-pointer group" onClick={() => navigate('/')}>
            <div className="w-10 h-10 bg-rutabaga-text rounded-full flex items-center justify-center mr-3 group-hover:scale-105 transition-transform">
              <Rabbit className="text-white w-6 h-6" />
            </div>
            <span className="font-heading font-bold text-xl sm:text-2xl text-rutabaga-text tracking-tight">
              Baga<span className="text-rutabaga-wood">Cam</span>
            </span>
          </div>

          {/* User Menu */}
          <div className="relative ml-3">
            <div>
              <button
                type="button"
                className="flex text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rutabaga-wood transition-transform active:scale-95"
                id="user-menu-button"
                aria-expanded={isMenuOpen}
                aria-haspopup="true"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                <span className="sr-only">Open user menu</span>
                <img
                  className="h-10 w-10 rounded-full object-cover border-2 border-rutabaga-wood"
                  src={user.avatar}
                  alt={user.name}
                  referrerPolicy="no-referrer"
                />
              </button>
            </div>

            {/* Dropdown */}
            {isMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40 cursor-default"
                  onClick={() => setIsMenuOpen(false)}
                />
                <div
                  className="origin-top-right absolute right-0 mt-2 w-48 rounded-xl shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50 transform transition-all duration-200 ease-out"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="user-menu-button"
                >
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm text-gray-500">Signed in as</p>
                    <p className="text-sm font-bold text-rutabaga-text truncate">{user.email}</p>
                  </div>

                  <button
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-rutabaga-bg flex items-center gap-2"
                    role="menuitem"
                    onClick={() => {
                      setIsMenuOpen(false);
                      navigate('/account');
                    }}
                  >
                    <UserIcon size={16} />
                    My Account
                  </button>

                  <button
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    role="menuitem"
                    onClick={() => {
                      setIsMenuOpen(false);
                      onSignOut();
                    }}
                  >
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};